// src/store/slices/planSlice.js

import { createSlice } from "@reduxjs/toolkit";
import {
  createPlanThunk,
  getAllPlansThunk,
  getPlanByIdThunk,
  getCustomerPlansThunk,
  getPlanScheduleThunk,
  getPlanLedgerThunk,
  recordPlanPaymentThunk,
  pausePlanThunk,
  resumePlanThunk,
  getPauseHistoryThunk,
  checkPauseEligibilityThunk,
  approvePauseThunk,
  rejectPauseThunk,
  getPendingPauseRequestsThunk,
  clearPlanError,
  clearPauseData,
  resetPlanState,
} from "../../thunks/jewelleryPlan";

const initialState = {
  // Plan data
  plans: [],
  selectedPlan: null,
  planSchedule: [],
  planLedger: [],

  // Filters
  filters: {
    status: null,
    branchId: null,
    customerId: null,
  },

  // Pagination
  pagination: {
    total: 0,
    page: 1,
    pages: 0,
  },

  // Pause-related state
  pauseEligibility: null,
  pauseHistory: [],
  pendingPauseRequests: [],
  pauseApproved: false,
  pauseRejected: false,
  pauseRequested: false,
  planResumed: false,

  // Loading states
  loading: false,
  pauseLoading: false,
  pauseActionLoading: false,
  pendingPauseLoading: false,

  // Error states
  error: null,
  pauseError: null,
};

const planSlice = createSlice({
  name: "plans",
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearSelectedPlan: (state) => {
      state.selectedPlan = null;
      state.planSchedule = [];
      state.planLedger = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // ==================== CREATE PLAN ====================
      .addCase(createPlanThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPlanThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.plans.unshift(action.payload.data);
      })
      .addCase(createPlanThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ==================== GET ALL PLANS ====================
      .addCase(getAllPlansThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllPlansThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.plans = action.payload.data;
        state.pagination = action.payload.pagination || {
          total: action.payload.data?.length || 0,
          page: 1,
          pages: 1,
        };
      })
      .addCase(getAllPlansThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ==================== GET PLAN BY ID ====================
      .addCase(getPlanByIdThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPlanByIdThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedPlan = action.payload.data.plan;
        state.planSchedule = action.payload.data.schedules || [];
        state.planLedger = [];
      })
      .addCase(getPlanByIdThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ==================== GET CUSTOMER PLANS ====================
      .addCase(getCustomerPlansThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCustomerPlansThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.plans = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(getCustomerPlansThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ==================== GET PLAN SCHEDULE ====================
      .addCase(getPlanScheduleThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getPlanScheduleThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.planSchedule = action.payload.data;
      })
      .addCase(getPlanScheduleThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ==================== GET PLAN LEDGER ====================
      .addCase(getPlanLedgerThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getPlanLedgerThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.planLedger = action.payload.data;
      })
      .addCase(getPlanLedgerThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ==================== RECORD PAYMENT ====================
      .addCase(recordPlanPaymentThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(recordPlanPaymentThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Optionally update the selected plan if payment was recorded
        if (state.selectedPlan?._id === action.meta.arg.planId) {
          // You might want to refetch plan details here
        }
      })
      .addCase(recordPlanPaymentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ==================== PAUSE PLAN ====================
      .addCase(pausePlanThunk.pending, (state) => {
        state.pauseLoading = true;
        state.pauseError = null;
        state.pauseRequested = false;
      })
      .addCase(pausePlanThunk.fulfilled, (state, action) => {
        state.pauseLoading = false;
        state.pauseRequested = true;

        // Update selected plan if it's the one being paused
        if (state.selectedPlan?._id === action.meta.arg.planId) {
          // If pause requires approval, don't update status yet
          if (!action.payload.requiresApproval) {
            state.selectedPlan.pauseMetrics = {
              ...state.selectedPlan.pauseMetrics,
              currentlyPaused: true,
            };
          }
        }

        // Update in plans list
        const planIndex = state.plans.findIndex(
          (p) => p._id === action.meta.arg.planId
        );
        if (planIndex !== -1 && !action.payload.requiresApproval) {
          state.plans[planIndex].pauseMetrics = {
            ...state.plans[planIndex].pauseMetrics,
            currentlyPaused: true,
          };
        }
      })
      .addCase(pausePlanThunk.rejected, (state, action) => {
        state.pauseLoading = false;
        state.pauseError = action.payload;
        state.pauseRequested = false;
      })

      // ==================== RESUME PLAN ====================
      .addCase(resumePlanThunk.pending, (state) => {
        state.pauseLoading = true;
        state.pauseError = null;
        state.planResumed = false;
      })
      .addCase(resumePlanThunk.fulfilled, (state, action) => {
        state.pauseLoading = false;
        state.planResumed = true;

        // Update selected plan
        if (state.selectedPlan?._id === action.meta.arg.planId) {
          state.selectedPlan.pauseMetrics = {
            ...state.selectedPlan.pauseMetrics,
            currentlyPaused: false,
          };
        }

        // Update in plans list
        const planIndex = state.plans.findIndex(
          (p) => p._id === action.meta.arg.planId
        );
        if (planIndex !== -1) {
          state.plans[planIndex].pauseMetrics = {
            ...state.plans[planIndex].pauseMetrics,
            currentlyPaused: false,
          };
        }
      })
      .addCase(resumePlanThunk.rejected, (state, action) => {
        state.pauseLoading = false;
        state.pauseError = action.payload;
        state.planResumed = false;
      })

      // ==================== CHECK PAUSE ELIGIBILITY ====================
      .addCase(checkPauseEligibilityThunk.pending, (state) => {
        state.pauseLoading = true;
        state.pauseError = null;
      })
      .addCase(checkPauseEligibilityThunk.fulfilled, (state, action) => {
        state.pauseLoading = false;
        state.pauseEligibility = action.payload.data;
      })
      .addCase(checkPauseEligibilityThunk.rejected, (state, action) => {
        state.pauseLoading = false;
        state.pauseError = action.payload;
      })

      // ==================== GET PAUSE HISTORY ====================
      .addCase(getPauseHistoryThunk.pending, (state) => {
        state.pauseLoading = true;
        state.pauseError = null;
      })
      .addCase(getPauseHistoryThunk.fulfilled, (state, action) => {
        state.pauseLoading = false;
        state.pauseHistory = action.payload.data.history;
        state.pauseEligibility = action.payload.data.eligibility;
      })
      .addCase(getPauseHistoryThunk.rejected, (state, action) => {
        state.pauseLoading = false;
        state.pauseError = action.payload;
      })

      // ==================== APPROVE PAUSE ====================
      .addCase(approvePauseThunk.pending, (state) => {
        state.pauseActionLoading = true;
        state.pauseError = null;
        state.pauseApproved = false;
      })
      .addCase(approvePauseThunk.fulfilled, (state, action) => {
        state.pauseActionLoading = false;
        state.pauseApproved = true;

        // Remove from pending list
        state.pendingPauseRequests = state.pendingPauseRequests.filter(
          (req) => req.pauseId !== action.meta.arg.pauseId
        );
      })
      .addCase(approvePauseThunk.rejected, (state, action) => {
        state.pauseActionLoading = false;
        state.pauseError = action.payload;
        state.pauseApproved = false;
      })

      // ==================== REJECT PAUSE (NEW) ====================
      .addCase(rejectPauseThunk.pending, (state) => {
        state.pauseActionLoading = true;
        state.pauseError = null;
        state.pauseRejected = false;
      })
      .addCase(rejectPauseThunk.fulfilled, (state, action) => {
        state.pauseActionLoading = false;
        state.pauseRejected = true;

        // Remove from pending list
        state.pendingPauseRequests = state.pendingPauseRequests.filter(
          (req) => req.pauseId !== action.meta.arg.pauseId
        );
      })
      .addCase(rejectPauseThunk.rejected, (state, action) => {
        state.pauseActionLoading = false;
        state.pauseError = action.payload;
        state.pauseRejected = false;
      })

      // ==================== GET PENDING PAUSE REQUESTS (NEW) ====================
      .addCase(getPendingPauseRequestsThunk.pending, (state) => {
        state.pendingPauseLoading = true;
        state.pauseError = null;
      })
      .addCase(getPendingPauseRequestsThunk.fulfilled, (state, action) => {
        state.pendingPauseLoading = false;
        state.pendingPauseRequests = action.payload.data;
      })
      .addCase(getPendingPauseRequestsThunk.rejected, (state, action) => {
        state.pendingPauseLoading = false;
        state.pauseError = action.payload;
      })

      // ==================== UTILITY ACTIONS ====================
      .addCase(clearPlanError, (state) => {
        state.error = null;
        state.pauseError = null;
      })
      .addCase(clearPauseData, (state) => {
        state.pauseEligibility = null;
        state.pauseHistory = [];
        state.pauseError = null;
        state.pauseApproved = false;
        state.pauseRejected = false;
        state.pauseRequested = false;
        state.planResumed = false;
      })
      .addCase(resetPlanState, () => initialState);
  },
});

export const { setFilters, clearSelectedPlan } = planSlice.actions;

export default planSlice.reducer;

// ==================== SELECTORS ====================

// Plan selectors
export const selectPlans = (state) => state.jewelleryPlan?.plans.plans;
export const selectSelectedPlan = (state) =>
  state.jewelleryPlan?.plans.selectedPlan;
export const selectPlanSchedule = (state) =>
  state.jewelleryPlan?.plans.planSchedule;
export const selectPlanLedger = (state) =>
  state.jewelleryPlan?.plans.planLedger;
export const selectPlanFilters = (state) => state.jewelleryPlan?.plans.filters;
export const selectPlanPagination = (state) =>
  state.jewelleryPlan?.plans.pagination;

// Pause selectors
export const selectPauseEligibility = (state) =>
  state.jewelleryPlan?.plans.pauseEligibility;
export const selectPauseHistory = (state) =>
  state.jewelleryPlan?.plans.pauseHistory;
export const selectPendingPauseRequests = (state) =>
  state.jewelleryPlan?.plans.pendingPauseRequests;
export const selectPauseApproved = (state) =>
  state.jewelleryPlan?.plans.pauseApproved;
export const selectPauseRejected = (state) =>
  state.jewelleryPlan?.plans.pauseRejected;
export const selectPauseRequested = (state) =>
  state.jewelleryPlan?.plans.pauseRequested;
export const selectPlanResumed = (state) =>
  state.jewelleryPlan?.plans.planResumed;

// Loading selectors
export const selectPlanLoading = (state) => state.jewelleryPlan?.plans.loading;
export const selectPauseLoading = (state) =>
  state.jewelleryPlan?.plans.pauseLoading;
export const selectPauseActionLoading = (state) =>
  state.jewelleryPlan?.plans.pauseActionLoading;
export const selectPendingPauseLoading = (state) =>
  state.jewelleryPlan?.plans.pendingPauseLoading;

// Error selectors
export const selectPlanError = (state) => state.jewelleryPlan?.plans.error;
export const selectPauseError = (state) =>
  state.jewelleryPlan?.plans.pauseError;

// Computed selectors
export const selectIsSelectedPlanPaused = (state) =>
  state.jewelleryPlan?.plans.selectedPlan?.pauseMetrics?.currentlyPaused ||
  false;

export const selectCanPausePlan = (state) =>
  state.jewelleryPlan?.plans.pauseEligibility?.eligible || false;

export const selectRemainingPauseMonths = (state) =>
  state.jewelleryPlan?.plans.pauseEligibility?.remainingPauseMonths || 0;
